import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    Box,
    Divider,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InventoryIcon from '@mui/icons-material/Inventory';
import {
    idGrupoPlanificacionOptions,
    grupoAlmacenamientoOptions,
    lineaKUTOptions,
    codigosProveedores,
    fetchBaseMeasureUnitCodeOptions,
    fetchValoracionCodeOptions,
    fetchCategoryOptions,
    fetchCodigosProveedores,
    fechaLocalHoy
} from '../../constantes/constants';
import { SapService } from '../../services/api';

const MaterialForm = ({ materials, setMaterials, selectedCategoria, selectedGrupoDePlanificacion, selectedLineaKUT, mostrarErrores = false }) => {
    const initialMaterial = {
        idregistros: materials[0]?.idregistros || "",
        id: 1,
        codigoMaterial: "",
        nombreItem: "Nuevo Material",
        categoriaProducto: selectedCategoria || "",
        baseMeasureUnitCode: "",
        lineaKUT: selectedLineaKUT || "",
        comentarioLineasDeVenta: "",
        idGrupoPlanificacion: selectedGrupoDePlanificacion || "",
        descripcionMaterial: "",
        proveedor: "",
        grupoAlmacenamiento: "",
        estado: "",
        error: "",
        codigoProveedor: "",
        valoracionProducto: "",
        fecha: fechaLocalHoy()
    };

    const [selectedMaterial, setSelectedMaterial] = useState(materials[0] || initialMaterial);

    useEffect(() => {
        if (materials.length > 0) {
            const exists = materials.find((m) => m.id === selectedMaterial.id);
            if (!exists) {
                setSelectedMaterial(materials[0]);
            } else {
                // Update selected material if it changed in the parent (e.g. bulk update)
                setSelectedMaterial(exists);
            }
        }
    }, [materials, selectedMaterial.id]);

    const addMaterial = () => {
        const newId = materials.length > 0 ? Math.max(...materials.map(m => m.id)) + 1 : 1;
        const newMaterial = {
            ...initialMaterial,
            id: newId,
            nombreItem: `Material ${newId}`
        };
        const newMaterials = [...materials, newMaterial];
        setMaterials(newMaterials);
        setSelectedMaterial(newMaterial);
    };

    const removeMaterial = (id, event) => {
        event.stopPropagation();
        if (materials.length === 1) return; // Prevent deleting the last one

        const filteredMaterials = materials.filter((m) => m.id !== id);
        setMaterials(filteredMaterials);
        if (filteredMaterials.length > 0) {
            if (selectedMaterial.id === id) {
                setSelectedMaterial(filteredMaterials[0]);
            }
        }
    };

    const handleChange = (field, value) => {
        // Determine updated material
        const updatedMaterial = { ...selectedMaterial, [field]: value };
        setSelectedMaterial(updatedMaterial);

        // Update master list
        setMaterials(
            materials.map((m) => (m.id === selectedMaterial.id ? updatedMaterial : m))
        );
    };

    const MAX_LENGTH = 40;
    const [searchCode, setSearchCode] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [proveedorInactivo, setProveedorInactivo] = useState(null);

    const [baseMeasureUnitCodeOptionsv2, setBaseMeasureUnitCodeOptionsv2] = useState([]);
    const [categoryOptionsv2, setCategoryOptionsV2] = useState([]);
    const [valoracionOptionsv2, setValoracionOptionsv2] = useState([]);
    const [providerCodesV2, setProviderCodesV2] = useState([]);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const [baseUnits, valoraciones, categories, provCodes] = await Promise.all([
                    fetchBaseMeasureUnitCodeOptions(),
                    fetchValoracionCodeOptions(),
                    fetchCategoryOptions(),
                    fetchCodigosProveedores()
                ]);
                setBaseMeasureUnitCodeOptionsv2(baseUnits);
                setValoracionOptionsv2(valoraciones);
                setCategoryOptionsV2(categories);
                setProviderCodesV2(provCodes);
            } catch (e) {
                console.error("Error loading options", e);
            }
        };
        loadOptions();
    }, []);

    const handleSearch = async () => {
        if (!searchCode) return;
        setLoading(true);
        setOpenDialog(true);
        setProveedorInactivo(null);

        try {
            const token = await SapService.login();
            const url = `https://my360131.sapbydesign.com/sap/byd/odata/cust/v1/khproveedores/SupplierCollection?$top=10&$format=json&$filter=startswith(SortingFormattedName,'${searchCode}')`;

            const value = await SapService.getData(url, token);

            const results = value.d.results.map(element => ({
                Proveedor: element.InternalID,
                Tproveedor: element.SortingFormattedName,
                idproveedor: providerCodesV2.find((p) => p.idproveedor === element.InternalID)?.value ?? "900",
                estado: element.LifeCycleStatusCodeText || "Sin estado",
                activo: element.LifeCycleStatusCode === "2"
            }));

            setSearchResults(results);
        } catch (error) {
            console.error("Error fetching providers:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectProvider = (provider) => {
        // Con un proveedor Obsoleto o Bloqueado SAP siempre rechaza el material
        // (PDM_SUPPLIER_INFO:004), así que no se asigna: se avisa que compras debe reactivarlo.
        if (!provider.activo) {
            setProveedorInactivo(provider);
            return;
        }
        setProveedorInactivo(null);
        const updatedMaterial = {
            ...selectedMaterial,
            proveedor: `${provider.Tproveedor}|${provider.idproveedor}|${provider.Proveedor}`
        };
        setSelectedMaterial(updatedMaterial);
        setMaterials(materials.map((m) => (m.id === selectedMaterial.id ? updatedMaterial : m)));

        setSearchCode("");
        setOpenDialog(false);
    };

    const uniqueCategoryOptions = Array.from(
        new Map(categoryOptionsv2.map((cat) => [cat.value, cat])).values()
    );

    const uniqueValoracion = Array.from(
        new Map(valoracionOptionsv2.map((cat) => [cat.value, cat])).values()
    );

    return (
        <Grid container spacing={3}>
            {/* Left Panel: Material List */}
            <Grid item xs={12} md={3}>
                <Paper elevation={0} variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
                    <Box p={2} borderBottom={1} borderColor="divider">
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            Lista de Materiales
                        </Typography>
                    </Box>

                    <List sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
                        {materials.map((material, index) => (
                            <ListItem
                                key={material.id}
                                disablePadding
                                sx={{ mb: 1 }}
                                secondaryAction={
                                    materials.length > 1 && (
                                        <IconButton size="small" onClick={(e) => removeMaterial(material.id, e)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )
                                }
                            >
                                <ListItemButton
                                    selected={selectedMaterial.id === material.id}
                                    onClick={() => setSelectedMaterial(material)}
                                    sx={{
                                        borderRadius: 2,
                                        '&.Mui-selected': {
                                            bgcolor: 'primary.light',
                                            color: 'primary.contrastText',
                                            '&:hover': {
                                                bgcolor: 'primary.main',
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 'bold'
                                            }
                                        },
                                    }}
                                >
                                    <InventoryIcon sx={{ mr: 1, fontSize: 20, opacity: 0.7 }} />
                                    <ListItemText
                                        primary={material.nombreItem || `Material ${index + 1}`}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            noWrap: true
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Box p={2} pt={1}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            fullWidth
                            onClick={addMaterial}
                            sx={{ borderStyle: 'dashed' }}
                        >
                            Agregar Material
                        </Button>
                    </Box>
                </Paper>
            </Grid>

            {/* Right Panel: Form */}
            <Grid item xs={12} md={9}>
                <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
                    <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold">
                            Detalles del Material
                        </Typography>
                        <Chip label={`ID: ${selectedMaterial.id}`} size="small" />
                    </Box>

                    <Grid container spacing={3}>
                        {/* Nombre del Ítem */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nombre del Ítem"
                                variant="outlined"
                                fullWidth
                                required
                                value={selectedMaterial.nombreItem ? selectedMaterial.nombreItem.toUpperCase() : ""}
                                onChange={(e) => handleChange("nombreItem", e.target.value.toUpperCase())}
                                placeholder="E.g. MARTILLO HIDRAULICO"
                                inputProps={{ maxLength: MAX_LENGTH }}
                                helperText={`${selectedMaterial.nombreItem.length}/${MAX_LENGTH}`}
                                error={selectedMaterial.nombreItem.length >= MAX_LENGTH}
                            />
                        </Grid>

                        {/* Proveedor Search */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Proveedor Asignado"
                                required
                                error={mostrarErrores && !selectedMaterial.proveedor}
                                helperText={!selectedMaterial.proveedor ? "Obligatorio: busque y seleccione un proveedor" : ""}
                                value={selectedMaterial.proveedor ? selectedMaterial.proveedor.split('|')[0] : (searchCode || "")}
                                onChange={(e) => setSearchCode(e.target.value)}
                                fullWidth
                                placeholder="Busque un proveedor..."
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {selectedMaterial.proveedor && (
                                                <IconButton onClick={() => handleChange("proveedor", "")} edge="end">
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                            <IconButton onClick={handleSearch} color="primary" disabled={!selectedMaterial.proveedor && !searchCode}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {selectedMaterial.proveedor && (
                                <Typography variant="caption" color="text.secondary">
                                    ID: {selectedMaterial.proveedor.split('|')[1]} | Int: {selectedMaterial.proveedor.split('|')[2]}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}><Divider /></Grid>

                        {/* Categoría */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                options={uniqueCategoryOptions}
                                getOptionLabel={(option) => option.label || ""}
                                value={uniqueCategoryOptions.find((cat) => cat.value === selectedMaterial.categoriaProducto) || null}
                                onChange={(_, newValue) => handleChange("categoriaProducto", newValue ? newValue.value : "")}
                                renderInput={(params) => <TextField {...params} label="Categoría de Producto" />}
                            />
                        </Grid>

                        {/* Unidad de Medida */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Unidad de medida"
                                value={selectedMaterial.baseMeasureUnitCode}
                                onChange={(e) => handleChange("baseMeasureUnitCode", e.target.value)}
                                fullWidth
                            >
                                {baseMeasureUnitCodeOptionsv2.map((code) => (
                                    <MenuItem key={code.value} value={code.value}>
                                        {code.nombre}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Línea KUT */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Línea de Producto (KUT)"
                                value={selectedMaterial.lineaKUT}
                                onChange={(e) => handleChange("lineaKUT", e.target.value)}
                                fullWidth
                            >
                                {lineaKUTOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Grupo de Planificación */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Grupo de Planificación"
                                value={selectedMaterial.idGrupoPlanificacion}
                                onChange={(e) => handleChange("idGrupoPlanificacion", e.target.value)}
                                fullWidth
                            >
                                {idGrupoPlanificacionOptions.map((grupo) => (
                                    <MenuItem key={grupo.value} value={grupo.value}>
                                        {grupo.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Valoración */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                options={uniqueValoracion}
                                getOptionLabel={(option) => option.label || ""}
                                value={uniqueValoracion.find((cat) => cat.value === selectedMaterial.valoracionProducto) || null}
                                onChange={(_, newValue) => handleChange("valoracionProducto", newValue ? newValue.value : "")}
                                renderInput={(params) => <TextField {...params} label="Valoración de Producto" />}
                            />
                        </Grid>

                        {/* Grupo Almacenamiento */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Grupo de Almacenamiento"
                                value={selectedMaterial.grupoAlmacenamiento}
                                onChange={(e) => handleChange("grupoAlmacenamiento", e.target.value)}
                                fullWidth
                            >
                                {grupoAlmacenamientoOptions.map((grupo) => (
                                    <MenuItem key={grupo.codigo} value={grupo.codigo}>
                                        {grupo.nombre}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Parte Numero */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Número de Parte (Código Proveedor)"
                                required
                                error={mostrarErrores && !String(selectedMaterial.codigoProveedor || "").trim()}
                                helperText={!String(selectedMaterial.codigoProveedor || "").trim() ? "Obligatorio: SAP no crea el material sin el número de parte del proveedor" : ""}
                                inputProps={{ maxLength: 60 }}
                                value={selectedMaterial.codigoProveedor}
                                onChange={(e) => handleChange("codigoProveedor", e.target.value)}
                                fullWidth
                            />
                        </Grid>

                        {/* Descripcion */}
                        <Grid item xs={12}>
                            <TextField
                                label="Descripción del Material"
                                value={selectedMaterial.descripcionMaterial}
                                onChange={(e) => handleChange("descripcionMaterial", e.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                                placeholder="Descripción detallada..."
                            />
                        </Grid>

                        {/* Comentarios */}
                        <Grid item xs={12}>
                            <TextField
                                label="Comentarios de Venta"
                                value={selectedMaterial.comentarioLineasDeVenta}
                                onChange={(e) => handleChange("comentarioLineasDeVenta", e.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                            />
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>

            {/* Search Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Búsqueda de Proveedor</DialogTitle>
                <DialogContent dividers>
                    {!loading && proveedorInactivo && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProveedorInactivo(null)}>
                            <strong>{proveedorInactivo.Tproveedor}</strong> (ID {proveedorInactivo.Proveedor}) está <strong>{proveedorInactivo.estado}</strong> en SAP
                            y no se puede usar: SAP rechaza la creación de materiales con proveedores inactivos.
                            Comuníquese con compras para que lo reactive, o elija otro proveedor.
                        </Alert>
                    )}
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : searchResults.length > 0 ? (
                        <List>
                            {searchResults.map((provider) => (
                                <ListItem key={provider.Proveedor} disablePadding>
                                    <ListItemButton onClick={() => handleSelectProvider(provider)}>
                                        <ListItemText
                                            primary={provider.Tproveedor}
                                            secondary={`ID: ${provider.Proveedor}`}
                                        />
                                        <Chip
                                            label={provider.estado}
                                            color={provider.activo ? "success" : "error"}
                                            size="small"
                                            variant={provider.activo ? "outlined" : "filled"}
                                            sx={{ ml: 2 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography align="center" color="text.secondary" p={2}>
                            No se encontraron resultados para "{searchCode}".
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default MaterialForm;
